import React, { ChangeEvent, useState } from 'react';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import PuffLoader from 'react-spinners/PuffLoader';
import { handleError } from '../../ui/err/handleError';
import { AxiosError } from 'axios';
import { AxiosCommonError } from '../../../shared/types';
import { useMutation } from '@tanstack/react-query';
import { passwordRex } from '../../../shared/utils/validation';
import Swal from 'sweetalert2';
import { inputLength, preventString } from '../../../shared/utils/validation';
import { authApis } from '../../../shared/apis/auth/apis';

const SignUp = () => {
  // useNavigate 선언
  const navigate = useNavigate();

  // react-hook-form 타입정의
  interface FormProps {
    name: string;
    email: string;
    password: string;
    passwordCheck: string;
    tel1: string;
    tel2: string;
    tel3: string;
  }

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormProps>({ mode: 'onChange' });

  const { mutate: mutateSignUp } = useMutation(authApis.signUp, {
    onSuccess: () => {
      Swal.fire({
        icon: 'success',
        title: '회원가입이 완료되었습니다.',
        text: '로그인 페이지로 이동합니다.',
      }).then(() => {
        navigate('/');
      });
    },
    onError: (error: AxiosError<AxiosCommonError>) => {
      handleError(error);
    },
  });

  // 폼 버튼 클릭시 작동하는 함수
  const onSubmit = async (data: FormProps) => {
    const sendData = {
      name: data.name,
      email: data.email,
      password: data.password,
      tel: `${data.tel1}-${data.tel2}-${data.tel3}`,
    };
    mutateSignUp(sendData);
  };

  // 캡스락 활성 여부 확인
  const [capslock, setCapsLock] = useState<boolean>(false);

  // 비밀번호 재확인
  const passwordRef = watch('password');

  return (
    <Wrap>
      <Container>
        <PuffLoader color="#fa5963" />
        <PostForm onSubmit={handleSubmit(onSubmit)}>
          <Line>
            <label htmlFor="">이름</label>
            <Input
              type="text"
              autoComplete="off"
              placeholder="이름을 입력해주세요"
              isInvalid={!!errors.name}
              {...register('name', {
                required: '이름을 입력해주세요',
              })}
            />
            {errors.name && <div className="err">{errors.name.message}</div>}
          </Line>
          <Line>
            <label htmlFor="email">이메일</label>
            <Input
              id="email"
              type="email"
              autoComplete="off"
              placeholder="이메일을 입력해주세요"
              isInvalid={!!errors.email}
              {...register('email', {
                required: '이메일을 입력해주세요',
              })}
            />
            {errors.email && <div className="err">{errors.email.message}</div>}
          </Line>
          <Line className="pw-line">
            <label htmlFor="password">비밀번호</label>
            <Input
              id="password"
              type="password"
              autoComplete="off"
              placeholder="영문/숫자/특수문자 포함 8~16자"
              isInvalid={!!errors.password}
              onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                e.getModifierState('CapsLock') === true ? setCapsLock(true) : setCapsLock(false);
              }}
              {...register('password', {
                required: '영문,숫자,특수문자(!@#$%^&*)를 1개 이상 조합하여 입력해주세요',
                pattern: {
                  value: passwordRex,
                  message: '비밀번호 형식에 맞지 않습니다.',
                },
              })}
            />
            {errors.password && <div className="err">{errors.password.message}</div>}
            {capslock && <div className="err capslock">CapsLock이 켜져 있습니다.</div>}
            <Input
              className="pw-check-input"
              type="password"
              autoComplete="off"
              placeholder="비밀번호를 한번 더 입력해주세요"
              isInvalid={!!errors.passwordCheck}
              {...register('passwordCheck', {
                required: '비밀번호를 한번 더 입력해주세요',
                validate: (value) => value === passwordRef || '비밀번호가 동일하지 않습니다.',
              })}
            />
            {errors.passwordCheck && <div className="err">{errors.passwordCheck.message}</div>}
          </Line>
          <Line>
            <label htmlFor="tel">휴대폰번호</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Input
                {...register('tel1', {
                  required: '휴대폰번호를 입력해주세요',
                })}
                maxLength={3}
                type="number"
                isInvalid={!!errors.tel1}
                onInput={(e: ChangeEvent<HTMLInputElement>) => inputLength(e)}
                onKeyDown={(e) => preventString(e)}
                placeholder="000"
              />
              <span>-</span>
              <Input
                {...register('tel2', {
                  required: '휴대폰번호를 입력해주세요',
                })}
                maxLength={4}
                type="number"
                isInvalid={!!errors.tel2}
                onInput={(e: ChangeEvent<HTMLInputElement>) => inputLength(e)}
                onKeyDown={(e) => preventString(e)}
                placeholder="0000"
              />
              <span>-</span>
              <Input
                {...register('tel3', {
                  required: '휴대폰번호를 입력해주세요',
                })}
                maxLength={4}
                type="number"
                isInvalid={!!errors.tel3}
                onInput={(e: ChangeEvent<HTMLInputElement>) => inputLength(e)}
                onKeyDown={(e) => preventString(e)}
                placeholder="0000"
              />
            </div>
            {(errors.tel1?.message || errors.tel2?.message || errors.tel3?.message) && (
              <div className="err" style={{ bottom: '-15px' }}>
                {errors.tel1?.message || errors.tel2?.message || errors.tel3?.message}
              </div>
            )}
          </Line>
          <Button>
            <button className="signUp-email">이메일로 회원가입 하기</button>
          </Button>
        </PostForm>
        <div className="signUp-redirection">
          이미 계정이 있으신가요?{' '}
          <span
            onClick={() => {
              navigate('/');
            }}
          >
            로그인하기
          </span>
        </div>
      </Container>
    </Wrap>
  );
};

export default SignUp;
const Wrap = styled.main`
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-size: 1rem;
`;
const Container = styled.main`
  width: 420px;
  min-height: 660px;
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  .signUp-redirection {
    width: 100%;
    margin-top: 10px;
    span {
      font-size: 15px;
      text-decoration: underline;
      cursor: pointer;
      color: ${(props) => props.theme.color.purple};
    }
  }
`;
const PostForm = styled.form`
  width: 100%;
  margin-top: 20px;
`;
const Line = styled.div`
  display: flex;
  flex-direction: column;
  margin: 10px 0;
  .err {
    color: ${(props) => props.theme.color.error};
    font-size: 1.2px;
    margin-top: 5px;
  }
  .err.capslock {
    color: ${(props) => props.theme.color.blue};
  }
  label {
    margin-bottom: 10px;
  }
  .pw-check-input {
    margin-top: 10px;
  }
`;
const Input = styled.input`
  border: ${(props) => props.theme.border.gray1};
  width: 100%;
  border-radius: 3px;
  height: 50px;
  padding: 10px;
  font-size: 14px;
  outline: ${(props: { isInvalid: boolean }) => props.isInvalid && 'none'};
  border: ${(props: { isInvalid: boolean }) => props.isInvalid && '1px solid red'};
  border-color: ${(props: { isInvalid: boolean }) => props.isInvalid && '#fa5963'};
  &:focus {
    border: ${(props) => props.theme.border.blue2};
    box-shadow: 0 0 0 2px ${(props) => props.theme.color.shadow.blue};
    outline: none;
  }
  &:focus::placeholder {
    color: transparent;
  }
`;
const Button = styled.div`
  margin-top: 20px;
  button {
    width: 100%;
    height: 50px;
    font-size: 15px;
  }
  .signUp-email {
    background-color: ${(props) => props.theme.color.red};
    /* background-color: #f2f2f2;
    color: #5e5e5e; */
  }
`;
